/**
 * PrivacyPolicy — public legal page.
 *
 * Lives at `/privacy`. Reachable without auth so Stripe can scrape it during
 * onboarding (Stripe blocks accounts that don't expose a public privacy URL).
 * Designed to match the LandingPageDark aesthetic (zinc/black, mono font for
 * footer accents) so it doesn't look like a stranded marketing afterthought.
 */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "May 24, 2026";
const SUPPORT_EMAIL = "support@countbucks.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="privacy-policy-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-black/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            data-testid="privacy-back-home"
          >
            <img src="/countbucks-icon.png" alt="CountBucks" className="w-7 h-7" />
            <span className="text-zinc-200 font-bold text-sm tracking-tight">CountBucks</span>
          </Link>
          <Link
            to="/"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1.5 font-mono"
          >
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="mb-12">
          <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Privacy Policy</h1>
          <p className="text-sm text-zinc-500 mt-3 font-mono">Last updated: {LAST_UPDATED}</p>
        </div>

        <article className="prose prose-invert prose-zinc max-w-none space-y-10">
          <Section title="1. Who we are">
            <p>
              CountBucks (“CountBucks”, “we”, “us”, “our”) operates the CountBucks platform — an
              AI-assisted business operating system for accounting, bookkeeping, and financial
              workflows. This Privacy Policy describes the personal information we collect when you
              use the service, why we collect it, who we share it with, and the choices you have.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <ul>
              <li>
                <strong>Account data.</strong> Your name, email, password hash, profile picture, and
                the workspace(s) you create or are invited to.
              </li>
              <li>
                <strong>Workspace data.</strong> Financial records you upload or generate while using
                CountBucks — invoices, bills, contacts, transactions, attachments, journal entries,
                tasks, conversations, and documents.
              </li>
              <li>
                <strong>Integration data.</strong> When you connect third-party services (e.g. Plaid
                for bank feeds, Stripe for payments, cloud storage providers for files, email
                delivery providers for transactional mail, large language model providers for AI),
                we receive only the data necessary for that feature to work.
              </li>
              <li>
                <strong>Usage data.</strong> Pages visited, features used, API requests, error logs,
                rough device + browser info, and IP address (used for security + diagnostics).
              </li>
              <li>
                <strong>AI prompts &amp; responses.</strong> Messages you send to CountBucks AI and
                the AI’s replies, including any tool calls the AI made on your behalf inside your
                workspace.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul>
              <li>To provide, maintain, and improve the CountBucks platform.</li>
              <li>To run authentication, billing, and account security.</li>
              <li>To power AI features (your prompts + relevant workspace data are sent to our LLM providers under contract).</li>
              <li>To send transactional emails (receipts, password resets, workspace invitations, weekly digests).</li>
              <li>To detect, prevent, and respond to fraud, abuse, and security incidents.</li>
              <li>To comply with legal obligations.</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your personal data, and we do <strong>not</strong> use
              your workspace data to train third-party AI models.
            </p>
          </Section>

          <Section title="4. Who we share information with">
            <p>
              We share data only with infrastructure providers that are bound by data-protection
              agreements. We disclose the categories of recipients below; the current list of
              specific providers is available on request to{' '}
              <a href="mailto:privacy@countbucks.com" className="underline hover:text-zinc-300">
                privacy@countbucks.com
              </a>.
            </p>
            <ul>
              <li><strong>Cloud storage providers</strong> — encrypted object storage for documents and attachments.</li>
              <li><strong>Database hosting providers</strong> — primary database for workspace records.</li>
              <li><strong>Stripe</strong> — billing; card data is held by Stripe (PCI-DSS Level 1), we never touch raw card numbers.</li>
              <li><strong>Plaid</strong> — bank connections; you authorize Plaid directly when linking an account.</li>
              <li><strong>AI model providers</strong> — AI model inference. Inputs are processed for the duration of a request and discarded; none of these providers may train on your inputs under our enterprise contracts.</li>
              <li><strong>Email delivery providers</strong> — transactional email delivery (account notifications, invoices, password resets).</li>
            </ul>
            <p>We disclose information to law enforcement only when compelled by valid legal process.</p>
          </Section>

          <Section title="5. Data retention">
            <p>
              Workspace data is retained for as long as your workspace exists. If you delete a
              workspace, we permanently purge its data from primary storage within 30 days and from
              backups within 90 days. Document attachments follow a default 7-year retention to
              support tax and audit requirements; you can request earlier deletion at any time.
            </p>
          </Section>

          <Section title="6. Your rights">
            <p>
              Depending on where you live, you have rights to access, correct, export, or delete
              your personal data, and to object to certain processing. Email{" "}
              <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>{" "}
              and we will respond within 30 days.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We encrypt data in transit (TLS 1.2+) and at rest. Passwords are hashed with bcrypt.
              Access to production systems is restricted to authorized personnel and audit-logged.
              No system is perfectly secure — if we discover a breach of your data, we will notify
              you without undue delay.
            </p>
          </Section>

          <Section title="8. International transfers">
            <p>
              CountBucks operates from the United States. By using the service you consent to your
              data being processed in the US and other jurisdictions where our providers operate.
              We rely on standard contractual clauses where required.
            </p>
          </Section>

          <Section title="9. Children">
            <p>
              CountBucks is not intended for children under 16. We do not knowingly collect personal
              information from children.
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. Material changes will be flagged
              in-app and via email. Continued use of CountBucks after an update means you accept
              the revised policy.
            </p>
          </Section>

          <Section title="11. Contact">
            <p>
              Questions? Email{" "}
              <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </article>

        <footer className="mt-20 pt-8 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500 font-mono">
          <span>© {new Date().getFullYear()} CountBucks</span>
          <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="privacy-to-terms">
            Terms of Service →
          </Link>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-white mb-3 tracking-tight">{title}</h2>
      <div className="text-sm leading-relaxed text-zinc-300 [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:mt-3 [&_p]:mt-3 [&_p:first-child]:mt-0 [&_strong]:text-white [&_strong]:font-semibold">
        {children}
      </div>
    </section>
  );
}
